import React, { useState, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "./reduxHooks";
import { sendMessage, updateUploadInTable } from "../ChatSlice_Store";
import { selectCurrentProjectId } from "../ProjectSlice_Store";
import { uploadFileToProject } from "../services/fileUploadService";
import {
  ALLOWED_EXTENSIONS_DISPLAY,
  MAX_FILE_SIZE_BYTES,
  MAX_FILE_SIZE_MB,
  isFileTypeAllowed,
  formatFileSize,
} from "../utils/fileUploadUtils";
import { getCurrentUserId } from "../utils/currentUser";

/** How long a drop error stays on screen. */
const ERROR_TOAST_MS = 5000;

const COMPLETE_PERCENT = 100;
const ID_RADIX = 36;

export interface DropUpload {
  isDraggingOver: boolean;
  dropError: string;
  setDropError: (message: string) => void;
  handleDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  handleDragLeave: (e: React.DragEvent<HTMLDivElement>) => void;
  handleDrop: (e: React.DragEvent<HTMLDivElement>) => Promise<void>;
}

/** Reject the whole drop if any file has an unsupported type or is oversized. */
function validationError(files: File[]): string | null {
  const invalidTypeFiles = files.filter((file) => !isFileTypeAllowed(file.name));
  if (invalidTypeFiles.length > 0) {
    const invalidNames = invalidTypeFiles.map((f) => f.name).join(", ");
    return `Unsupported file type: ${invalidNames}. Allowed formats: ${ALLOWED_EXTENSIONS_DISPLAY}`;
  }

  const oversizedFiles = files.filter((file) => file.size > MAX_FILE_SIZE_BYTES);
  if (oversizedFiles.length === 1) {
    const file = oversizedFiles[0];
    return `"${file.name}" (${formatFileSize(file.size)}) exceeds the ${MAX_FILE_SIZE_MB} MB limit.`;
  }
  if (oversizedFiles.length > 1) {
    const fileList = oversizedFiles.map((f) => `${f.name} (${formatFileSize(f.size)})`).join(", ");
    return `${oversizedFiles.length} files exceed the ${MAX_FILE_SIZE_MB} MB limit: ${fileList}`;
  }

  return null;
}

/** True once the pointer has left the container itself, not just moved onto a child. */
function hasLeftContainer(e: React.DragEvent<HTMLDivElement>): boolean {
  const rect = e.currentTarget.getBoundingClientRect();
  return e.clientX < rect.left || e.clientX >= rect.right || e.clientY < rect.top || e.clientY >= rect.bottom;
}

interface UploadContext {
  dispatch: ReturnType<typeof useAppDispatch>;
  username: string;
  projectId: string | null;
}

/** Push a single dropped file to the project's blob store, reporting progress. */
async function uploadDroppedFile(file: File, ctx: UploadContext): Promise<void> {
  const { dispatch, projectId } = ctx;
  const uploadId = `upload-${Date.now()}-${Math.random().toString(ID_RADIX).substring(2, 9)}`;

  dispatch(
    updateUploadInTable({
      uploadId,
      fileName: file.name,
      fileSize: file.size,
      progress: 0,
      status: "Preparing upload...",
      completed: false,
      error: false,
    })
  );

  try {
    const { filePath } = await uploadFileToProject({
      file,
      username: ctx.username,
      projectId,
      onStatusChange: (status: string) => {
        if (status === "fetching_sas") {
          dispatch(updateUploadInTable({ uploadId, status: "Getting upload URL..." }));
        }
        if (status === "uploading") {
          dispatch(updateUploadInTable({ uploadId, status: "Uploading file..." }));
        }
      },
      onProgress: ({ percent }: { percent: number | null }) => {
        if (percent === null || percent === undefined) return;
        dispatch(updateUploadInTable({ uploadId, progress: percent }));
      },
    });

    if (projectId && filePath) {
      dispatch(
        updateUploadInTable({
          uploadId,
          progress: COMPLETE_PERCENT,
          status: "Uploaded. Waiting for processing...",
          completed: false,
        })
      );

      void dispatch(sendMessage(`CONTROL_FILE_UPLOAD:${projectId}:${filePath}`));
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
    dispatch(
      updateUploadInTable({
        uploadId,
        status: `Upload failed: ${errorMessage}`,
        error: true,
      })
    );
  }
}

/** Drag-and-drop file uploads for the chat surface. */
export function useDropUpload(isDropEnabled: boolean): DropUpload {
  const dispatch = useAppDispatch();
  const currentProjectId = useAppSelector(selectCurrentProjectId);
  const reduxAuth = useAppSelector((state) => state.userAuthAndAdminSlice);

  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [dropError, setDropError] = useState<string>("");

  // Auto-dismiss drop error after 5 seconds
  useEffect(() => {
    if (dropError) {
      const timer = setTimeout(() => setDropError(""), ERROR_TOAST_MS);
      return () => clearTimeout(timer);
    }
  }, [dropError]);

  const uploadContext: UploadContext = {
    dispatch,
    username: getCurrentUserId(reduxAuth),
    projectId: currentProjectId,
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();

    // Only show overlay if uploading is enabled and files are being dragged
    if (!isDropEnabled) return;
    if (!e.dataTransfer.types.includes("Files")) return;

    setIsDraggingOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();

    if (hasLeftContainer(e)) {
      setIsDraggingOver(false);
    }
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);

    // Silent ignore if upload is disabled
    if (!isDropEnabled) return;

    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length === 0) return;

    const invalid = validationError(droppedFiles);
    if (invalid) {
      setDropError(invalid);
      return;
    }

    // Clear any previous error and upload all files in parallel
    setDropError("");
    const uploadResults = await Promise.allSettled(
      droppedFiles.map((f) => uploadDroppedFile(f, uploadContext))
    );

    // Surface which files failed without interrupting other uploads
    const failedFiles = uploadResults
      .map((result, index) => (result.status === "rejected" ? droppedFiles[index].name : null))
      .filter((name): name is string => name !== null);

    if (failedFiles.length > 0) {
      setDropError(`Failed to upload: ${failedFiles.join(", ")}`);
    }
  };

  return { isDraggingOver, dropError, setDropError, handleDragOver, handleDragLeave, handleDrop };
}
